/** The wizard's steps, in the order they are offered. */
export const RELATIONSHIP_STEPS = ['people', 'objects', 'attributes', 'review'] as const;

export type RelationshipStep = (typeof RELATIONSHIP_STEPS)[number];

/** The step the tablist reported back, or undefined when it names none. */
export function toRelationshipStep(value: string | null): RelationshipStep | undefined {
  return RELATIONSHIP_STEPS.find((step) => step === value);
}

export type NavStep = {
  value: RelationshipStep;
  title: string;
  isDisabled: boolean;
  /** Turns the step's check icon green once the step is satisfied. */
  isComplete: boolean;
  /** Explains a disabled step; absent when the step needs no explanation. */
  disabledMessage?: string;
};

export type NavStepsInput = {
  step: RelationshipStep;
  /** The Objects step is dropped when the caller already chose the object. */
  hasPreselectedObject: boolean;
  isLoading: boolean;
  peopleCount: number;
  objectCount: number;
  objectTypeName: string;
  /** Workspace term for people, title-cased plural — for the step title. */
  peopleTitlePlural: string;
  /** Workspace term for people, plural — for the disabled explanation. */
  peoplePlural: string;
};

export function buildNavSteps(input: NavStepsInput): NavStep[] {
  const needsPeople = input.peopleCount === 0;
  const needsObjects = input.objectCount === 0;
  // Which selection the reader still owes is whichever step they are looking at.
  const laterStepsMessage = `Select ${input.step === 'people' ? input.peoplePlural : 'objects'} before moving on to the next steps.`;
  const laterStepsDisabled = input.isLoading || needsPeople || needsObjects;

  return [
    {
      value: 'people',
      title: `Select ${input.peopleTitlePlural}`,
      isDisabled: input.isLoading,
      isComplete: input.peopleCount > 0,
    },
    ...(input.hasPreselectedObject
      ? []
      : [
          {
            value: 'objects' as const,
            title: `Select ${input.objectTypeName}`,
            isDisabled: input.isLoading || needsPeople,
            isComplete: input.objectCount > 0,
            disabledMessage: `Select ${input.peoplePlural} before moving on to the next steps.`,
          },
        ]),
    {
      value: 'attributes',
      title: 'Add attributes',
      isDisabled: laterStepsDisabled,
      // Attributes are optional, so the step only reads as done once the
      // reader has moved past it.
      isComplete: input.step === 'review',
      disabledMessage: laterStepsMessage,
    },
    {
      value: 'review',
      title: 'Review',
      isDisabled: laterStepsDisabled,
      isComplete: false,
      disabledMessage: laterStepsMessage,
    },
  ];
}

/** The step after `step`, or `null` on the last one (where the footer submits). */
export function stepAfter(steps: readonly NavStep[], step: RelationshipStep): RelationshipStep | null {
  return steps[steps.findIndex((candidate) => candidate.value === step) + 1]?.value ?? null;
}

/** The step before `step`, or `null` on the first one (where the footer cancels). */
export function stepBefore(steps: readonly NavStep[], step: RelationshipStep): RelationshipStep | null {
  const index = steps.findIndex((candidate) => candidate.value === step);
  return index <= 0 ? null : (steps[index - 1]?.value ?? null);
}

/** Whether the footer's advance button is blocked by an empty selection. */
export function isAdvanceBlocked(step: RelationshipStep, peopleCount: number, objectCount: number): boolean {
  if (step === 'people') return peopleCount === 0;
  if (step === 'objects') return objectCount === 0;
  return false;
}
